<script lang="ts" setup>
import type { FormSubmitEvent } from '@nuxt/ui';
import * as yup from 'yup';

// Types
type Schema = yup.InferType<typeof schema>;

// Composables
const { areaId } = storeToRefs(baselineStore());
const { getAreaWithAnEnergyUseType } = baselineServices();
const { t } = useI18n();

// Props & Emits
const emit = defineEmits<{
  (e: 'next'): void;
}>();

// Data

const form = useTemplateRef('form');
const items = computed(() => {
  return getAreaWithAnEnergyUseType().map((area) => ({
    label: area.name,
    data: {
      type: t(`baseline.energy_use_types.${area.energy_use_type}_name`),
    },
    value: area.id,
    icon: 'i-heroicons-cpu-chip',
  }));
});

// Form
const schema = yup.object({
  areaId: yup.number().required(t('global.form.required')),
});
const state = reactive<{
  areaId: number | undefined;
}>({
  areaId: undefined,
});

// Methods
const submit = (event: FormSubmitEvent<Schema>) => {
  areaId.value = event.data.areaId;
  emit('next');
};

onMounted(() => {
  state.areaId = areaId.value;
});
</script>
<template>
  <u-form ref="form" :state="state" value-key="value" :schema="schema" @submit="submit">
    <div class="w-full bg-white rounded-lg p-4 border border-gray-200">
      <!-- Title -->
      <h2 class="text-lg font-bold">
        {{ $t('baseline.energy_use') }}
      </h2>
      <p class="text-sm text-gray-500">
        {{ $t('baseline.energy_use_description') }}
      </p>

      <u-alert
        v-if="items.length === 0"
        color="neutral"
        variant="subtle"
        class="mt-4"
        :title="$t('baseline.energy_use_no_area_found')"
        :actions="[
          {
            label: $t('baseline.energy_use_no_area_found_action'),
            onClick: () => {
              navigateTo('/configuration/areas');
            },
          },
        ]"
      />

      <!-- Form -->
      <div v-else class="mt-4">
        <u-radio-group
          v-model="state.areaId"
          name="areaId"
          indicator="end"
          variant="card"
          :items="items"
          :ui="{ fieldset: 'grid grid-cols-2 gap-4' }"
        >
          <template #label="{ item }">
            <div class="flex items-center gap-2 select-none">
              <div
                class="rounded-lg h-10 w-10 flex items-center justify-center p-2"
                :class="[item.value === state.areaId ? 'bg-primary-200' : 'bg-gray-100']"
              >
                <u-icon
                  :name="item.icon"
                  class="w-full h-full"
                  :class="[item.value === state.areaId ? 'text-primary-600' : 'text-gray-500']"
                />
              </div>
              <div class="flex items-start flex-col ml-4">
                <div class="flex items-start flex-col font-semibold text-xs">
                  {{ item.label }}
                  <u-badge color="neutral" size="sm" variant="subtle" class="mt-1">{{ item.data.type }}</u-badge>
                </div>
              </div>
            </div>
          </template>
        </u-radio-group>
        <p v-if="form?.errors" class="text-red-500 text-sm mt-2">
          {{ form?.errors[0]?.message }}
        </p>
      </div>
    </div>

    <div class="w-full mt-8 flex flex-row justify-between">
      <u-button color="neutral" variant="outline" :label="$t('global.cancel')" />
      <div class="flex items-center gap-2">
        <u-button color="primary" variant="solid" :disabled="items.length === 0" type="submit" :label="$t('global.next')" />
      </div>
    </div>
  </u-form>
</template>
